import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { type EOnboardingTaskType, TemplateClass } from '@feathr/blackbox';
import { Button, ButtonValid, Input, Modal, showError } from '@feathr/components';
import { templatesBlankToClassMap } from '@feathr/extender/components/TemplateSelect';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';

interface ICreateEmailTemplateModalProps {
  onClose: () => void;
  onComplete?: (taskType: EOnboardingTaskType) => void;
}

import blankTemplate from '@feathr/extender/components/TemplateSelect/blankTemplate.json';

function CreateEmailTemplateModal({
  onClose,
}: Readonly<ICreateEmailTemplateModalProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { Templates } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const [templateName, setTemplateName] = useState<string>('');

  async function handleCreate(): Promise<void> {
    const blankIndex = templatesBlankToClassMap[TemplateClass.PinpointEmail];
    const template = Templates.create({
      ...blankTemplate[blankIndex],
      id: undefined,
      name: templateName,
      _cls: TemplateClass.PinpointEmail,
      account: account.id,
      default: false,
      parent_kind: 'account',
      parent: account.id,
    });

    try {
      const response = await Templates.add(template, { validate: false });
      // TODO: Redirect to proper account level edit page: #5539
      history.push(`${localUrl(response.getItemUrl('edit/design'))}?onboarding=true`);
    } catch (error) {
      showError(error, t);
    } finally {
      onClose();
    }
  }

  function validateTemplateName(): string[] {
    const errors: string[] = [];
    if (!templateName.trim()) {
      errors.push(t("Template name can't be blank"));
    }
    return errors;
  }

  function handleTemplateNameChange(newValue?: string): void {
    setTemplateName(newValue ?? '');
  }

  const validationErrors = validateTemplateName();

  return (
    <Modal
      onClose={onClose}
      opened={true}
      rightActions={
        <>
          <Button onClick={onClose}>{t('Back')}</Button>
          <ButtonValid errors={validationErrors} onClick={handleCreate} type={'primary'}>
            {t('Create')}
          </ButtonValid>
        </>
      }
      size={'md'}
      title={t('Create email template')}
    >
      <div>
        <Input
          helpText={t("Create a descriptive name that identifies this template's purpose.")}
          label={t('Template name')}
          onChange={handleTemplateNameChange}
          placeholder={t('Enter a template name')}
          type={'text'}
          value={templateName}
        />
      </div>
    </Modal>
  );
}

export default CreateEmailTemplateModal;
